/**
 * @file MultiTenantDashboardPage.js
 * @description Main page used to display Dashboards which return data for the specific client for Multi Tenant Admin users.
 */

import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import { getMultitenantDashboardAllData } from '../../services/dashboardService';
import { fetchClients } from '../../services/clientService';
import { fetchAgencies } from '../../services/systemService';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { useAuthContext } from '../../store/Contexts';
import TextRangeDatePicker from '../helpers/TextRangeDatePicker';
import SideDrawer from '../helpers/SideDrawer';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import {
    DASHBOARD_POPULATION_GROUPS_MAX,
    DASHBOARD_TOOLTIP_MESSAGE_TEXT,
    DASHBOARD_DEFAULT_START_DATE,
    DASHBOARD_DEFAULT_END_DATE,
    DASHBOARD_DEFAULT_SPINNER_WIDTH,
    DASHBOARD_DEFAULT_DATA,
    DASHBOARD_DEFAULT_LOADING_STATE,
    MULTITENANT_DASHBOARD_REPORT_DEFINITIONS_SECTIONS,
    buildDashboardLoadingState,
    mapDashboardResponseToCardData,
    exportDashboardAllCardsCsvZip,
    exportDashboardCardCsv,
    exportDashboardReportPdf,
} from './DashboardUtils';
import { DashboardTypeEnum, TenantStatus } from '../../utils/data/enums';
import CardContainer from './CardContainer';
import Registration from './Registration';
import ClientStatus from './ClientStatus';
import ClientsPerTier from './ClientsPerTier';
import SessionsByDevice from './SessionsByDevice';
import SessionsDaily from './SessionsDaily';
import MessagesUserPreference from './MessagesUserPreference';
import './DashboardPage.scss';
import { getText } from '../../i18n';

const DASHBOARD_TYPES = Object.values(DashboardTypeEnum);
const MULTITENANT_DASHBOARD_TYPES = [
    DashboardTypeEnum.Registration,
    DashboardTypeEnum.ClientStatus,
    DashboardTypeEnum.ClientTier,
    DashboardTypeEnum.SessionsDaily,
    DashboardTypeEnum.SessionsByDevice,
    DashboardTypeEnum.MessagesUserPreference,
];

const DASHBOARD_RESPONSE_FIELD_BY_TYPE = {
    [DashboardTypeEnum.Registration]: 'registration',
    [DashboardTypeEnum.ClientStatus]: 'clientStatus',
    [DashboardTypeEnum.ClientTier]: 'clientTier',
    [DashboardTypeEnum.SessionsDaily]: 'sessionsDaily',
    [DashboardTypeEnum.SessionsByDevice]: 'sessionsByDevice',
    [DashboardTypeEnum.MessagesUserPreference]: 'messagesUserPreference',
};

const ALL_CLIENTS_OPTION_ID = 'all-clients-option';
const FAVORITED_CLIENTS_OPTION_ID = 'favorited-clients-option';
const ALL_AGENCIES_OPTION_ID = 'all-agencies-option';

const ClientSelectionMode = {
    All: 'all',
    Favorites: 'favorites',
    Custom: 'custom',
};

const ALL_AGENCIES_OPTION = {
    id: ALL_AGENCIES_OPTION_ID,
    name: 'All Agencies',
};

const isGlobalAgency = agency => {
    return agency?.code?.toLowerCase() === 'global';
};

const isAllAgenciesOption = agency => {
    return agency?.id === ALL_AGENCIES_OPTION_ID;
};

const sortClientsByName = clientList => {
    return [...clientList].sort((a, b) => a.name.localeCompare(b.name));
};

const getClientsForAgency = (clientList, agency) => {
    if (!Array.isArray(clientList)) {
        return [];
    }

    if (!agency || isGlobalAgency(agency) || isAllAgenciesOption(agency)) {
        return sortClientsByName(clientList);
    }

    const agencyClients = clientList.filter(
        client => Number(client.agencyId) === Number(agency.id),
    );
    return sortClientsByName(agencyClients);
};

const buildClientDropdownOptions = agencyClients => {
    return [
        {
            id: ALL_CLIENTS_OPTION_ID,
            name: 'All Clients',
            isPinnedTop: true,
        },
        {
            id: FAVORITED_CLIENTS_OPTION_ID,
            name: 'Favorited Clients',
            isPinnedTop: true,
        },
        ...agencyClients,
    ];
};

const MultiTenantDashboardPage = () => {
    const [startDate, setStartDate] = useState(DASHBOARD_DEFAULT_START_DATE);
    const [endDate, setEndDate] = useState(DASHBOARD_DEFAULT_END_DATE);
    const [clientState, setClientState] = useState({
        clients: [],
        selected: [],
        hasInitialized: false,
    });
    const [showAllClients, setShowAllClients] = useState(false);
    const [dashboardData, setDashboardData] = useState(DASHBOARD_DEFAULT_DATA);
    const [loadingState, setLoadingState] = useState(
        DASHBOARD_DEFAULT_LOADING_STATE,
    );
    const [isExportingReport, setIsExportingReport] = useState(false);
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const downloadMenuRef = useRef(null);
    const dashboardPageClassName = `dashboard-page${
        isExportingReport ? ' exporting-report' : ''
    }`;
    const [favoriteClients, setFavoriteClients] = useState([]);
    const [clients, setClients] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState(null);
    const [clientSelectionMode, setClientSelectionMode] = useState(
        ClientSelectionMode.All,
    );
    const { user } = useAuthContext();
    const [isAgencyDropdownOpen, setIsAgencyDropdownOpen] = useState(false);
    const [agencySearchTerm, setAgencySearchTerm] = useState('');
    const agencyDropdownRef = useRef(null);

    const userAgencyIds = useMemo(() => {
        return Array.isArray(user?.jwt?.agencies) ? user.jwt.agencies : [];
    }, [user?.jwt]);
    const userClientIds = useMemo(() => {
        return Array.isArray(user?.jwt?.clients) ? user.jwt.clients : [];
    }, [user?.jwt]);
    const assignedAgencyCount = useMemo(() => {
        return new Set(userAgencyIds.map(agencyId => Number(agencyId))).size;
    }, [userAgencyIds]);
    const hasGlobalAgencyAccess = useMemo(() => {
        return userAgencyIds.some(agencyId => Number(agencyId) === 0);
    }, [userAgencyIds]);

    useEffect(() => {
        const handleOutsideClick = event => {
            if (!downloadMenuRef.current?.contains(event.target)) {
                setIsDownloadMenuOpen(false);
            }
            if (!agencyDropdownRef.current?.contains(event.target)) {
                setIsAgencyDropdownOpen(false);
                setAgencySearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    // Update client state and sort on selection.
    const updateClients = newSelection => {
        const selectedAgencyClients = getClientsForAgency(
            clients,
            selectedAgency,
        );
        const selectedAgencyFavoriteClients = getClientsForAgency(
            favoriteClients,
            selectedAgency,
        );
        const selectedAgencyClientById = new Map(
            selectedAgencyClients.map(client => [client.id, client]),
        );
        const selectedIds = newSelection.map(selection => selection.id);
        const hasAllClientsOption = selectedIds.includes(ALL_CLIENTS_OPTION_ID);
        const hasAllWithOtherSelections =
            hasAllClientsOption && newSelection.length > 1;
        const wasInAllMode = clientSelectionMode === ClientSelectionMode.All;

        if (hasAllWithOtherSelections && !wasInAllMode) {
            setClientSelectionMode(ClientSelectionMode.All);
            setShowAllClients(false);
            setClientState(prevState => ({
                ...prevState,
                selected: selectedAgencyClients,
                hasInitialized: true,
            }));
            return;
        }

        const normalizedSelection =
            hasAllWithOtherSelections && wasInAllMode
                ? newSelection.filter(
                      selection => selection.id !== ALL_CLIENTS_OPTION_ID,
                  )
                : newSelection;
        const normalizedIds = normalizedSelection.map(
            selection => selection.id,
        );
        const hasAllClientsOptionNormalized = normalizedIds.includes(
            ALL_CLIENTS_OPTION_ID,
        );
        const hasFavoritedClientsOptionNormalized = normalizedIds.includes(
            FAVORITED_CLIENTS_OPTION_ID,
        );

        const selectedRegularClients = normalizedSelection
            .filter(
                selection =>
                    selection.id !== ALL_CLIENTS_OPTION_ID &&
                    selection.id !== FAVORITED_CLIENTS_OPTION_ID,
            )
            .map(selection => selectedAgencyClientById.get(selection.id))
            .filter(Boolean);

        const setSelectedClients = (nextSelectedClients, mode) => {
            setClientSelectionMode(mode);
            setShowAllClients(false);
            setClientState(prevState => ({
                ...prevState,
                selected: nextSelectedClients,
                hasInitialized: true,
            }));
        };

        if (hasAllClientsOptionNormalized) {
            setSelectedClients(selectedAgencyClients, ClientSelectionMode.All);
            return;
        }

        if (hasFavoritedClientsOptionNormalized) {
            if (clientSelectionMode !== ClientSelectionMode.Favorites) {
                if (selectedAgencyFavoriteClients.length > 0) {
                    setSelectedClients(
                        selectedAgencyFavoriteClients,
                        ClientSelectionMode.Favorites,
                    );
                    return;
                }

                setSelectedClients(
                    selectedAgencyClients,
                    ClientSelectionMode.All,
                );
                return;
            }

            const favoriteClientIds = new Set(
                selectedAgencyFavoriteClients.map(client => client.id),
            );
            const hasExactFavoriteClientSelection =
                selectedRegularClients.length ===
                    selectedAgencyFavoriteClients.length &&
                selectedRegularClients.every(client =>
                    favoriteClientIds.has(client.id),
                );

            if (
                selectedRegularClients.length === 0 ||
                hasExactFavoriteClientSelection
            ) {
                if (selectedAgencyFavoriteClients.length > 0) {
                    setSelectedClients(
                        selectedAgencyFavoriteClients,
                        ClientSelectionMode.Favorites,
                    );
                    return;
                }

                setSelectedClients(
                    selectedAgencyClients,
                    ClientSelectionMode.All,
                );
                return;
            }

            setSelectedClients(
                selectedRegularClients,
                ClientSelectionMode.Custom,
            );
            return;
        }

        if (selectedRegularClients.length === 0) {
            setSelectedClients(selectedAgencyClients, ClientSelectionMode.All);
            return;
        }

        setSelectedClients(selectedRegularClients, ClientSelectionMode.Custom);
    };

    const handleRemoveClient = client => {
        if (clientState.selected.length <= 1) {
            return;
        }

        const updatedSelectedClients = clientState.selected.filter(
            currentClient => currentClient.id !== client.id,
        );
        setClientSelectionMode(ClientSelectionMode.Custom);
        setClientState(prevState => ({
            ...prevState,
            selected: updatedSelectedClients,
        }));
    };

    const loadDashboardData = useCallback(async () => {
        const selectedClientIds = clientState.selected.map(client => client.id);
        if (selectedClientIds.length === 0) {
            setDashboardData(DASHBOARD_DEFAULT_DATA);
            return;
        }

        setLoadingState(buildDashboardLoadingState(DASHBOARD_TYPES, true));
        try {
            const allDashboardResponse = await getMultitenantDashboardAllData(
                selectedClientIds,
                startDate,
                endDate,
            );
            const mappedDashboardData = mapDashboardResponseToCardData(
                DASHBOARD_TYPES,
                DASHBOARD_RESPONSE_FIELD_BY_TYPE,
                allDashboardResponse,
                DASHBOARD_DEFAULT_DATA,
            );
            setDashboardData(mappedDashboardData);
        } catch (error) {
            setDashboardData(DASHBOARD_DEFAULT_DATA);
            Toast.error(getText('msg_1b7685d77c37'));
        } finally {
            setLoadingState(buildDashboardLoadingState(DASHBOARD_TYPES, false));
        }
    }, [clientState.selected, startDate, endDate]);

    useEffect(() => {
        const loadDashboardFilters = async () => {
            try {
                const [fetchedClients, fetchedAgencies] = await Promise.all([
                    fetchClients([
                        TenantStatus.Live,
                        TenantStatus.InProgress,
                        TenantStatus.Contracting,
                        TenantStatus.Prelaunch,
                    ]),
                    fetchAgencies(),
                ]);

                const nextClients = Array.isArray(fetchedClients)
                    ? fetchedClients
                    : [];
                const allowedClientIds = new Set(
                    userClientIds.map(clientId => Number(clientId)),
                );
                const nextClientsWithAccess = nextClients.filter(client =>
                    allowedClientIds.has(Number(client.id)),
                );
                const nextFavoriteClients = nextClientsWithAccess.filter(
                    client => client.isFavorite,
                );
                const allAgencies = Array.isArray(fetchedAgencies?.viewModels)
                    ? [...fetchedAgencies.viewModels].sort((a, b) =>
                          a.name.localeCompare(b.name),
                      )
                    : [];
                const allowedAgencyIds = new Set(
                    userAgencyIds.map(agencyId => Number(agencyId)),
                );
                const nextAgencies = hasGlobalAgencyAccess
                    ? allAgencies
                    : allAgencies.filter(agency =>
                          allowedAgencyIds.has(Number(agency.id)),
                      );
                const defaultAgency =
                    hasGlobalAgencyAccess || assignedAgencyCount > 1
                        ? ALL_AGENCIES_OPTION
                        : nextAgencies[0] || null;
                const defaultAgencyClients = defaultAgency
                    ? getClientsForAgency(nextClientsWithAccess, defaultAgency)
                    : [];

                setClients(nextClientsWithAccess);
                setFavoriteClients(nextFavoriteClients);
                setAgencies(nextAgencies);
                setSelectedAgency(defaultAgency);
                setClientSelectionMode(ClientSelectionMode.All);
                setClientState({
                    clients: buildClientDropdownOptions(defaultAgencyClients),
                    selected: defaultAgencyClients,
                    hasInitialized: true,
                });
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error('Failed to initialize dashboard filters', error);
                Toast.error(getText('msg_165625363afd'));
            }
        };

        loadDashboardFilters();
    }, [
        assignedAgencyCount,
        hasGlobalAgencyAccess,
        userAgencyIds,
        userClientIds,
    ]);

    useEffect(() => {
        const noClientsSelected = clientState.selected.length === 0;
        if (noClientsSelected) {
            setDashboardData(DASHBOARD_DEFAULT_DATA);
            setLoadingState(buildDashboardLoadingState(DASHBOARD_TYPES, false));
            return;
        }

        loadDashboardData();
    }, [
        startDate,
        endDate,
        clientState.selected,
        clientState.hasInitialized,
        loadDashboardData,
    ]);

    const handleAgencyChange = selectedAgencyId => {
        if (String(selectedAgencyId) === ALL_AGENCIES_OPTION_ID) {
            const allAgencyClients = getClientsForAgency(clients, null);
            setSelectedAgency(ALL_AGENCIES_OPTION);
            setShowAllClients(false);
            setClientSelectionMode(ClientSelectionMode.All);
            setClientState(prevState => ({
                ...prevState,
                clients: buildClientDropdownOptions(allAgencyClients),
                selected: allAgencyClients,
                hasInitialized: true,
            }));
            return;
        }

        const nextAgency = agencies.find(
            agency =>
                String(agency.id) === String(selectedAgencyId) ||
                agency.name === selectedAgencyId,
        );

        if (!nextAgency) {
            return;
        }

        const nextAgencyClients = getClientsForAgency(clients, nextAgency);
        setSelectedAgency(nextAgency);
        setShowAllClients(false);
        setClientSelectionMode(ClientSelectionMode.All);
        setClientState(prevState => ({
            ...prevState,
            clients: buildClientDropdownOptions(nextAgencyClients),
            selected: nextAgencyClients,
            hasInitialized: true,
        }));
    };

    // Report download method to download a snapshot of the Dashboard page.
    const handleDownloadReport = async () => {
        if (isExportingReport) {
            return;
        }

        setIsExportingReport(true);
        setShowAllClients(true);
        try {
            await exportDashboardReportPdf();
            logEvent(LogEventType.DataDownloaded, `Dashboard data downloaded`);
        } catch (error) {
            Toast.error(getText('msg_fd5b354e8798'));
        } finally {
            setShowAllClients(false);
            setIsExportingReport(false);
        }
    };

    const handleDownloadAllDashboardCsv = async () => {
        if (isExportingReport) {
            return;
        }

        setShowAllClients(true);
        setIsDownloadMenuOpen(false);
        try {
            await exportDashboardAllCardsCsvZip(
                dashboardData,
                MULTITENANT_DASHBOARD_TYPES,
            );
            logEvent(
                LogEventType.DataDownloaded,
                `Dashboard CSV data downloaded`,
            );
        } catch (error) {
            Toast.error(getText('msg_a77526eed3b7'));
        } finally {
            setShowAllClients(false);
        }
    };

    const handleDownloadPdfReport = async () => {
        setIsDownloadMenuOpen(false);
        await handleDownloadReport();
    };

    const handleToggleDownloadMenu = () => {
        setIsDownloadMenuOpen(current => !current);
    };

    const handleDownloadDashboardCardCsv = async dashboardTypeId => {
        try {
            await exportDashboardCardCsv(
                dashboardTypeId,
                dashboardData[dashboardTypeId],
            );
        } catch (error) {
            Toast.error(getText('msg_f6c8080d9637'));
        }
    };

    const canChangeAgency = hasGlobalAgencyAccess || assignedAgencyCount > 1;
    const nonGlobalAgencies = agencies.filter(
        agency => !isGlobalAgency(agency),
    );
    const agencyOptions = canChangeAgency
        ? [ALL_AGENCIES_OPTION, ...nonGlobalAgencies]
        : nonGlobalAgencies;
    const filteredAgencyOptions = agencyOptions.filter(agency =>
        agency.name.toLowerCase().includes(agencySearchTerm.toLowerCase()),
    );
    const allClientsOption = clientState.clients.find(
        client => client.id === ALL_CLIENTS_OPTION_ID,
    );
    const favoritedClientsOption = clientState.clients.find(
        client => client.id === FAVORITED_CLIENTS_OPTION_ID,
    );
    const selectedClientDropdownOptions =
        clientSelectionMode === ClientSelectionMode.All
            ? allClientsOption
                ? [allClientsOption]
                : []
            : clientSelectionMode === ClientSelectionMode.Favorites
              ? favoritedClientsOption
                  ? [favoritedClientsOption, ...clientState.selected]
                  : clientState.selected
              : clientState.selected;

    return (
        <div>
            <div
                className={dashboardPageClassName}
                data-dashboard-export-root="true"
            >
                <div className="dashboard-header-container">
                    <div className="dashboard-header-top">
                        <div className="dashboard-header-left">
                            <div
                                className="dashboard-agency-text-trigger"
                                ref={agencyDropdownRef}
                            >
                                <div
                                    className={`agency-text-label${
                                        canChangeAgency ? '' : ' no-dropdown'
                                    }`}
                                    onClick={() =>
                                        canChangeAgency &&
                                        setIsAgencyDropdownOpen(prev => !prev)
                                    }
                                    role={
                                        canChangeAgency ? 'button' : undefined
                                    }
                                    tabIndex={canChangeAgency ? 0 : undefined}
                                    onKeyDown={e =>
                                        canChangeAgency &&
                                        e.key === 'Enter' &&
                                        setIsAgencyDropdownOpen(prev => !prev)
                                    }
                                >
                                    {selectedAgency?.name || 'Select Agency'}
                                    {canChangeAgency && (
                                        <i
                                            className={`bi bi-chevron-${
                                                isAgencyDropdownOpen
                                                    ? 'up'
                                                    : 'down'
                                            }`}
                                        />
                                    )}
                                </div>
                                {canChangeAgency && isAgencyDropdownOpen && (
                                    <div className="agency-dropdown-panel">
                                        <div className="agency-dropdown-search">
                                            <i className="bi bi-search" />
                                            <input
                                                type="text"
                                                placeholder={getText(
                                                    'msg_bce06414177f',
                                                )}
                                                value={agencySearchTerm}
                                                onChange={e =>
                                                    setAgencySearchTerm(
                                                        e.target.value,
                                                    )
                                                }
                                                autoFocus
                                            />
                                        </div>
                                        <hr className="agency-dropdown-divider" />
                                        <div className="agency-dropdown-list">
                                            {filteredAgencyOptions.map(
                                                agency => (
                                                    <div
                                                        key={agency.id}
                                                        className="agency-dropdown-item"
                                                        onClick={() => {
                                                            handleAgencyChange(
                                                                agency.id,
                                                            );
                                                            setIsAgencyDropdownOpen(
                                                                false,
                                                            );
                                                            setAgencySearchTerm(
                                                                '',
                                                            );
                                                        }}
                                                        role="option"
                                                        aria-selected={
                                                            selectedAgency?.id ===
                                                            agency.id
                                                        }
                                                    >
                                                        {agency.name}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className="dashboard-main-download-actions"
                            ref={downloadMenuRef}
                        >
                            <button
                                className="ing-button-primary-left align-self-baseline"
                                onClick={handleToggleDownloadMenu}
                                aria-busy={isExportingReport}
                                type="button"
                            >
                                {getText('msg_a479c9c34e87')}
                            </button>
                            {isDownloadMenuOpen && (
                                <div className="dashboard-main-download-menu">
                                    <button
                                        className="dashboard-main-download-menu-item"
                                        onClick={handleDownloadPdfReport}
                                        type="button"
                                    >
                                        {getText('msg_d613d88cb2d8')}
                                    </button>
                                    <button
                                        className="dashboard-main-download-menu-item"
                                        onClick={handleDownloadAllDashboardCsv}
                                        type="button"
                                    >
                                        {getText('msg_3281188351b8')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="date-picker-container">
                        <TextRangeDatePicker
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            hideDropdownIndicators={isExportingReport}
                        />
                        <div
                            className="view-report-definitions"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            {getText('msg_83db64acf280')}
                        </div>
                    </div>
                    <div className="population-groups">
                        <div className="dropdown-population-groups-container">
                            {isExportingReport ? (
                                <div className="population-groups-label">
                                    {getText('msg_0a1a49474923')}
                                </div>
                            ) : (
                                <SearchableDropdown
                                    key={selectedAgency?.id ?? 'all'}
                                    placeholderLabel={getText(
                                        'msg_28e22fe3dde5',
                                    )}
                                    options={clientState.clients}
                                    optionIDField="id"
                                    savedOptionIDField="id"
                                    updateOptions={updateClients}
                                    savedOptions={selectedClientDropdownOptions}
                                    showTags={false}
                                    minSelections={1}
                                    controlledSelection
                                />
                            )}
                        </div>
                        <div className="population-group-chips">
                            {clientSelectionMode ===
                                ClientSelectionMode.All && (
                                <span
                                    className="badge-base badge-population-group population-group-chip"
                                    style={{
                                        height: '23px',
                                        paddingTop: '3px',
                                    }}
                                >
                                    {getText('msg_6318be1134d3')}
                                </span>
                            )}
                            {clientSelectionMode ===
                                ClientSelectionMode.Favorites && (
                                <span
                                    className="badge-base badge-population-group population-group-chip"
                                    style={{
                                        height: '23px',
                                        paddingTop: '3px',
                                    }}
                                >
                                    {getText('msg_9ad812f73e02')}
                                </span>
                            )}
                            {clientSelectionMode ===
                                ClientSelectionMode.Custom &&
                                clientState.selected
                                    .slice(
                                        0,
                                        showAllClients
                                            ? clientState.selected.length
                                            : DASHBOARD_POPULATION_GROUPS_MAX,
                                    )
                                    .map(client => (
                                        <span
                                            key={client.id}
                                            className="badge-base badge-population-group population-group-chip"
                                            style={{
                                                height: '23px',
                                                paddingTop: '3px',
                                            }}
                                        >
                                            {clientState.selected.length > 1 &&
                                                !isExportingReport && (
                                                    <i
                                                        className="bi bi-x-lg"
                                                        style={{
                                                            paddingRight: '6px',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() =>
                                                            handleRemoveClient(
                                                                client,
                                                            )
                                                        }
                                                    />
                                                )}
                                            {client.name}
                                        </span>
                                    ))}
                            {clientSelectionMode ===
                                ClientSelectionMode.Custom &&
                                clientState.selected.length >
                                    DASHBOARD_POPULATION_GROUPS_MAX &&
                                !showAllClients && (
                                    <div
                                        className="show-more"
                                        onClick={() => setShowAllClients(true)}
                                    >
                                        +
                                        {clientState.selected.length -
                                            DASHBOARD_POPULATION_GROUPS_MAX}{' '}
                                        {getText('msg_4fd94bf46ca1')}
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
                <div>
                    <div className="dashboard-card-container">
                        <CardContainer
                            title={getText('msg_b233e775d63b')}
                            onDownloadCsv={() =>
                                handleDownloadDashboardCardCsv(
                                    DashboardTypeEnum.Registration,
                                )
                            }
                            toolTipText={
                                DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                    DashboardTypeEnum.Registration
                                ]
                            }
                        >
                            {loadingState[DashboardTypeEnum.Registration] ? (
                                <Spinner
                                    width={DASHBOARD_DEFAULT_SPINNER_WIDTH}
                                    className="dashboard-loading-spinner"
                                />
                            ) : (
                                <Registration
                                    registrationData={
                                        dashboardData[
                                            DashboardTypeEnum.Registration
                                        ]
                                    }
                                    endDate={endDate}
                                    isMultiTenant={true}
                                />
                            )}
                        </CardContainer>
                        <CardContainer
                            title={getText('msg_b972c339ffd3')}
                            onDownloadCsv={() =>
                                handleDownloadDashboardCardCsv(
                                    DashboardTypeEnum.ClientStatus,
                                )
                            }
                            toolTipText={
                                DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                    DashboardTypeEnum.ClientStatus
                                ]
                            }
                        >
                            {loadingState[DashboardTypeEnum.ClientStatus] ? (
                                <Spinner
                                    width={DASHBOARD_DEFAULT_SPINNER_WIDTH}
                                    className="dashboard-loading-spinner"
                                />
                            ) : (
                                <ClientStatus
                                    clientStatusData={
                                        dashboardData[
                                            DashboardTypeEnum.ClientStatus
                                        ]
                                    }
                                    endDate={endDate}
                                />
                            )}
                        </CardContainer>
                    </div>
                    <div className="dashboard-card-container">
                        <CardContainer
                            title={getText('msg_0150ad8f4ec2')}
                            onDownloadCsv={() =>
                                handleDownloadDashboardCardCsv(
                                    DashboardTypeEnum.ClientTier,
                                )
                            }
                            toolTipText={
                                DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                    DashboardTypeEnum.ClientTier
                                ]
                            }
                        >
                            {loadingState[DashboardTypeEnum.ClientTier] ? (
                                <Spinner
                                    width={DASHBOARD_DEFAULT_SPINNER_WIDTH}
                                    className="dashboard-loading-spinner"
                                />
                            ) : (
                                <ClientsPerTier
                                    clientTierData={
                                        dashboardData[
                                            DashboardTypeEnum.ClientTier
                                        ]
                                    }
                                    endDate={endDate}
                                />
                            )}
                        </CardContainer>
                        <CardContainer
                            title={getText('msg_a12fd0292dce')}
                            onDownloadCsv={() =>
                                handleDownloadDashboardCardCsv(
                                    DashboardTypeEnum.SessionsDaily,
                                )
                            }
                            toolTipText={
                                DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                    DashboardTypeEnum.SessionsDaily
                                ]
                            }
                        >
                            {loadingState[DashboardTypeEnum.SessionsDaily] ? (
                                <Spinner
                                    width={DASHBOARD_DEFAULT_SPINNER_WIDTH}
                                    className="dashboard-loading-spinner"
                                />
                            ) : (
                                <SessionsDaily
                                    sessionsDailyData={
                                        dashboardData[
                                            DashboardTypeEnum.SessionsDaily
                                        ]
                                    }
                                    startDate={startDate}
                                    endDate={endDate}
                                />
                            )}
                        </CardContainer>
                    </div>
                    <div className="dashboard-card-container">
                        <CardContainer
                            title={getText('msg_0179bc349141')}
                            onDownloadCsv={() =>
                                handleDownloadDashboardCardCsv(
                                    DashboardTypeEnum.SessionsByDevice,
                                )
                            }
                            toolTipText={
                                DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                    DashboardTypeEnum.SessionsByDevice
                                ]
                            }
                        >
                            {loadingState[
                                DashboardTypeEnum.SessionsByDevice
                            ] ? (
                                <Spinner
                                    width={DASHBOARD_DEFAULT_SPINNER_WIDTH}
                                    className="dashboard-loading-spinner"
                                />
                            ) : (
                                <SessionsByDevice
                                    sessionsByDeviceData={
                                        dashboardData[
                                            DashboardTypeEnum.SessionsByDevice
                                        ]
                                    }
                                />
                            )}
                        </CardContainer>
                        <CardContainer
                            title={getText('msg_08237d4d18a3')}
                            onDownloadCsv={() =>
                                handleDownloadDashboardCardCsv(
                                    DashboardTypeEnum.MessagesUserPreference,
                                )
                            }
                            toolTipText={
                                DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                    DashboardTypeEnum.MessagesUserPreference
                                ]
                            }
                        >
                            {loadingState[
                                DashboardTypeEnum.MessagesUserPreference
                            ] ? (
                                <Spinner
                                    width={DASHBOARD_DEFAULT_SPINNER_WIDTH}
                                    className="dashboard-loading-spinner"
                                />
                            ) : (
                                <MessagesUserPreference
                                    messagesUserPreferenceData={
                                        dashboardData[
                                            DashboardTypeEnum
                                                .MessagesUserPreference
                                        ]
                                    }
                                    endDate={endDate}
                                />
                            )}
                        </CardContainer>
                    </div>
                </div>
                <div data>
                    <SideDrawer
                        isOpen={isDrawerOpen}
                        setIsOpen={setIsDrawerOpen}
                        header={getText('msg_c60bb0cab7d8')}
                        sections={
                            MULTITENANT_DASHBOARD_REPORT_DEFINITIONS_SECTIONS
                        }
                    />
                </div>
            </div>
            {isExportingReport && (
                <div
                    className="dashboard-export-overlay"
                    role="status"
                    aria-live="polite"
                >
                    <div className="dashboard-export-overlay-content">
                        <div className="dashboard-export-overlay-title">
                            {getText('msg_63499997499d')}
                        </div>
                        <div className="dashboard-export-overlay-subtitle">
                            {getText('msg_d9e3c8dd911e')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiTenantDashboardPage;
